// Layout.tsx
import React, { useEffect, useRef } from 'react';

// Hooks
import { useSetupAppDisplaySettings } from '../../hooks/useSetupAppDisplaySettings';

// React swipe hook
import { useSwipeable } from 'react-swipeable';

// React Router
import { useLocation } from 'react-router-dom';

// Redux hooks and selectors
import { useAppSelector } from '../../redux/hooks/useAppSelector';
import { useAppDispatch } from '../../redux/hooks/useAppDispatch';
import {
  selectIsRTL,
  selectIsSidebarOpen,
} from '../../redux/selectors/displaySettingsSelectors';
import { toggleSidebar } from '../../redux/slices/displaySettingsSlice';

// Custom Components
import { Header } from '../Header/Header';
import { Sidebar } from '../Sidebar/Sidebar';
import { Toast } from '../Shared/Toast/Toast';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  useSetupAppDisplaySettings();

  const dispatch = useAppDispatch();
  const location = useLocation();
  const isSidebarOpen = useAppSelector(selectIsSidebarOpen);
  const isRtl = useAppSelector(selectIsRTL);
  const prevPathname = useRef(location.pathname);

  // Close sidebar on route change (for mobile)
  useEffect(() => {
    if (prevPathname.current !== location.pathname && isSidebarOpen) {
      dispatch(toggleSidebar());
    }
    prevPathname.current = location.pathname;
  }, [location.pathname, isSidebarOpen, dispatch]);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (isRtl) {
        if (!isSidebarOpen) {
          dispatch(toggleSidebar());
        }
      } else {
        if (isSidebarOpen) {
          dispatch(toggleSidebar());
        }
      }
    },
    onSwipedRight: () => {
      if (isRtl) {
        if (isSidebarOpen) {
          dispatch(toggleSidebar());
        }
      } else {
        if (!isSidebarOpen) {
          dispatch(toggleSidebar());
        }
      }
    },
    preventScrollOnSwipe: true,
    trackTouch: true,
  });

  return (
    <div className="flex h-screen flex-col">
      <Header />
      <div
        {...swipeHandlers}
        className="relative flex w-full flex-1 overflow-hidden lg:gap-4"
      >
        <Sidebar />
        <main className="mt-8 flex-1 overflow-y-auto px-8 text-white">
          <Toast />
          {children}
        </main>
      </div>
    </div>
  );
};
